/**
 * Advanced text comparison using dynamic programming to find
 * more accurate text differences (Levenshtein distance based)
 * @param {string} text1 - The first text
 * @param {string} text2 - The second text
 * @returns {Object} Object containing differences and statistics
 */
export function advancedCompareTexts(text1, text2) {
  // Normalize whitespace and split into words
  const words1 =
    text1.length > 0 ? text1.replace(/\s+/g, " ").trim().split(" ") : [];
  const words2 =
    text2.length > 0 ? text2.replace(/\s+/g, " ").trim().split(" ") : [];

  // Build the edit distance matrix
  const m = words1.length;
  const n = words2.length;
  const dp = Array(m + 1)
    .fill()
    .map(() => Array(n + 1).fill(0));

  // Initialize first row and column
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  // Build the edit distance matrix
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (words1[i - 1].toLowerCase() === words2[j - 1].toLowerCase()) {
        dp[i][j] = dp[i - 1][j - 1]; // No operation needed
      } else {
        dp[i][j] =
          1 +
          Math.min(
            dp[i - 1][j], // Deletion
            dp[i][j - 1], // Insertion
            dp[i - 1][j - 1] // Substitution
          );
      }
    }
  }

  // Backtrack to find actual operations
  let i = m;
  let j = n;
  const operations = [];

  while (i > 0 || j > 0) {
    if (
      i > 0 &&
      j > 0 &&
      words1[i - 1].toLowerCase() === words2[j - 1].toLowerCase()
    ) {
      // Same word, no operation
      i--;
      j--;
    } else if (i > 0 && j > 0 && dp[i][j] === dp[i - 1][j - 1] + 1) {
      // Substitution (change)
      operations.push({
        from: words1[i - 1],
        to: words2[j - 1],
      });
      i--;
      j--;
    } else if (i > 0 && dp[i][j] === dp[i - 1][j] + 1) {
      // Deletion from text1
      operations.push({
        word: words1[i - 1],
      });
      i--;
    } else {
      // Insertion in text1 (deletion from text2)
      operations.push({
        word: words2[j - 1],
      });
      j--;
    }
  }

  // Calculate statistics
  const totalWords = Math.max(words1.length, words2.length);
  const totalChanges = operations.length;
  const changePercentage =
    totalWords > 0 ? (totalChanges / totalWords) * 100 : 0;

  return {
    totalWords,
    totalChanges,
    changePercentage: parseFloat(changePercentage.toFixed(1)),
  };
}
